/**
 * Import field summary component
 *
 * Displays which fields were used, captured, and ignored during parsing
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { IoCheckmarkCircle, IoChevronDown, IoChevronUp, IoCloseCircle, IoInformationCircle } from 'react-icons/io5';

import type { ImportFieldSummary as FieldSummary } from '@/lib/universalCSVParser';

export interface ImportFieldSummaryProps {
  summary: FieldSummary;
  className?: string;
}

export default function ImportFieldSummary({ summary, className }: ImportFieldSummaryProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);

  // Handle optional arrays with defaults
  const missingFields = summary.fieldsMissing ?? [];

  // Determine if the summary has meaningful content to show
  const hasContent = summary.fieldsUsed.length > 0 || summary.fieldsIgnored.length > 0 || missingFields.length > 0;

  if (!hasContent) {
    return <></>;
  }

  // Build header text based on source name and version
  const headerText = summary.formatVersion
    ? `Import Summary (${summary.sourceName} ${summary.formatVersion})`
    : `Import Summary (${summary.sourceName})`;

  return (
    <div className={clsx('bg-slate/50 border-storm/50 rounded-lg border', className)}>
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="hover:bg-storm/30 flex w-full items-center justify-between px-4 py-3 text-left transition-colors"
      >
        <div className="flex items-center gap-3">
          <IoInformationCircle className="text-aether h-5 w-5" />
          <span className="text-pearl text-sm font-medium">{headerText}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick counts */}
          <div className="hidden items-center gap-3 text-xs sm:flex">
            <span className="text-success flex items-center gap-1">
              <IoCheckmarkCircle className="h-3.5 w-3.5" />
              {summary.fieldsUsed.length} used
            </span>
            {summary.fieldsIgnored.length > 0 && (
              <span className="text-ash flex items-center gap-1">
                <IoCloseCircle className="h-3.5 w-3.5" />
                {summary.fieldsIgnored.length} ignored
              </span>
            )}
          </div>
          {isExpanded ? <IoChevronUp className="text-ash h-5 w-5" /> : <IoChevronDown className="text-ash h-5 w-5" />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-storm/50 space-y-3 border-t px-4 py-3">
          {/* Fields Used */}
          {summary.fieldsUsed.length > 0 && (
            <FieldSection
              icon={<IoCheckmarkCircle className="text-success h-4 w-4" />}
              label="Fields Used"
              description="Data extracted from these columns"
              fields={summary.fieldsUsed}
              tagColor="success"
            />
          )}

          {/* Fields Ignored */}
          {summary.fieldsIgnored.length > 0 && (
            <FieldSection
              icon={<IoCloseCircle className="text-ash h-4 w-4" />}
              label="Fields Ignored"
              description="Not needed for import"
              fields={summary.fieldsIgnored}
              tagColor="ash"
            />
          )}

          {/* Optional Fields Missing */}
          {missingFields.length > 0 && (
            <FieldSection
              icon={<IoInformationCircle className="text-gold h-4 w-4" />}
              label="Optional Fields Missing"
              description="Not present in your export"
              fields={missingFields}
              tagColor="gold"
            />
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

interface FieldSectionProps {
  icon: ReactElement;
  label: string;
  description: string;
  fields: string[];
  tagColor: 'success' | 'aether' | 'ash' | 'gold';
}

function FieldSection({ icon, label, description, fields, tagColor }: FieldSectionProps): ReactElement {
  const tagClasses: Record<string, string> = {
    success: 'bg-success/20 text-success',
    aether: 'bg-aether/20 text-aether',
    ash: 'bg-slate text-ash',
    gold: 'bg-gold/20 text-gold',
  };

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        {icon}
        <span className="text-pearl text-sm font-medium">{label}</span>
        <span className="text-ash text-xs">({fields.length})</span>
      </div>
      <p className="text-ash mb-2 ml-6 text-xs">{description}</p>
      <div className="ml-6 flex flex-wrap gap-1.5">
        {fields.map((field) => (
          <span key={field} className={clsx('rounded px-2 py-0.5 font-mono text-xs', tagClasses[tagColor])}>
            {field}
          </span>
        ))}
      </div>
    </div>
  );
}
